import React, { useMemo } from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSelfCanonical } from "@/lib/seo";
import { MarketingNav } from "@/components/MarketingNav";
import { MarketingFooter } from "@/components/MarketingFooter";
import { Mic, FileText, Heart, Check, ArrowRight, ShieldCheck } from "lucide-react";

// Marketing detail pages for the landing feature cards. Public (logged-out)
// and caregiver-web only. Content/layout adapted from the marketing site.
const FEATURES = {
  "claims-appeals": {
    icon: FileText,
    eyebrow: "Claims & appeals engine",
    title: "The EOB workflow that pays for itself.",
    lede: "Snap the bill. Our AI classifies every line item, flags balance-billing and No Surprises Act traps, and drafts an appeal letter you can review, email as a PDF, and send.",
    bullets: [
      "AI-powered OCR keeps 100% of the numbers — no manual re-entry.",
      "Drafts assertive or measured appeal letters, per claim.",
      "Email the finished appeal PDF to yourself, the insurer, or family in one tap.",
      "Every letter is versioned so a redraft never overwrites your original.",
      "Risk scoring surfaces the claims most worth fighting first.",
    ],
    callout: "Families recover an average of $412 per denied claim in beta cohorts.",
    visual: (
      <div className="rounded-xl border border-line p-4 text-sm">
        <span className="inline-block rounded-full px-2.5 py-0.5 text-xs font-bold bg-mint-tint text-mint-dark">HIGH RISK · Balance billing</span>
        <h4 className="mt-2 font-semibold text-ink">Regional Medical Center · $2,847.19 billed</h4>
        <p className="mt-2 text-xs text-subtle">Out-of-network anesthesiologist at an in-network facility. NSA §300gg-131 applies.</p>
        <div className="mt-3 flex items-center gap-2">
          <span className="rounded-lg border border-line px-2 py-1 text-xs text-ink">Draft appeal ✏️</span>
          <span className="rounded-lg border border-line px-2 py-1 text-xs text-ink">Email PDF</span>
        </div>
      </div>
    ),
  },
  "ambient-scribe": {
    icon: Mic,
    eyebrow: "Ambient visit scribe",
    title: "Never lose a follow-up in a paper bag again.",
    lede: "Tap record at the appointment — a 15-second consent gate keeps it HIPAA-conscious. When the visit ends, you get a clean summary with the diagnosis, med changes, and follow-up tasks for your circle.",
    bullets: [
      "Consent capture — with signature and device ID — stored per session.",
      "AI transcription tuned for medical vocabulary.",
      "Auto-extracts to-dos, med changes, and open questions into your feeds.",
      "New meds sync straight to the Meds tab — no double entry.",
      "Audio stays private unless you explicitly choose to share it.",
    ],
    visual: (
      <div className="space-y-3">
        <div className="rounded-xl border border-line p-3 text-sm"><b className="text-ink">Follow-up:</b> Repeat A1C in 90 days<div className="mt-1 text-xs text-subtle">→ Assigned to Maya · Due Sep 3</div></div>
        <div className="rounded-xl border border-line p-3 text-sm"><b className="text-ink">Med change:</b> Metformin 500 mg → 1000 mg BID<div className="mt-1 text-xs text-subtle">→ Auto-updated in Meds tab</div></div>
        <div className="rounded-xl border border-line p-3 text-sm"><b className="text-ink">Question for provider:</b> Ok to skip the walk during a heatwave?</div>
      </div>
    ),
  },
  "care-circle": {
    icon: Heart,
    eyebrow: "Unified care circle",
    title: "One feed. Everyone stays looped in.",
    lede: "Primary caregiver, secondary caregivers, paid aides, siblings, viewers — each with their own role and permissions. Meds, calendar, vitals, and activity live on one shared surface.",
    bullets: [
      "Roles: primary caregiver, caregiver, aide, family, viewer.",
      "Per-recipient silos — a hired aide only sees the person they help.",
      "Recurring events and custom reminders (daily, weekly, monthly).",
      "A real-time activity feed so anyone can catch up in 30 seconds.",
      "Aide shift sign-offs keep a clear record of who did what.",
    ],
    visual: (
      <div className="space-y-3">
        <div className="rounded-xl border border-line p-3 text-sm"><b className="text-ink">Maya</b> signed off the morning aide shift<div className="mt-1 text-xs text-subtle">→ &quot;Ate breakfast, meds taken, good mood&quot; · 9:12a</div></div>
        <div className="rounded-xl border border-line p-3 text-sm"><b className="text-ink">Reminder:</b> Cardiology follow-up<div className="mt-1 text-xs text-subtle">→ Tue 2:30p · assigned to David</div></div>
        <div className="rounded-xl border border-line p-3 text-sm"><b className="text-ink">Vitals:</b> BP 128/82 logged<div className="mt-1 text-xs text-subtle">→ within target range</div></div>
      </div>
    ),
  },
};

export default function Feature() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const f = FEATURES[slug];
  const others = useMemo(() => Object.entries(FEATURES).filter(([k]) => k !== slug), [slug]);
  useSelfCanonical(f ? `${f.eyebrow} · KinBridge Health` : null);
  if (!f) return <Navigate to="/" replace />;
  const Icon = f.icon;

  return (
    <div className="min-h-screen bg-surface">
      {/* Header */}
      <div className="bg-brand-dark">
        <div className="max-w-5xl mx-auto px-6">
          <MarketingNav />
        </div>

        {/* Hero */}
        <div className="max-w-5xl mx-auto px-6 pt-6 pb-16 kb-fade-up">
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-5">
            <Icon className="w-6 h-6 text-mint" />
          </div>
          <p className="text-mint text-xs font-bold uppercase tracking-widest">{f.eyebrow}</p>
          <h1 className="font-display text-4xl sm:text-5xl text-white leading-[1.08] mt-3 max-w-2xl">{f.title}</h1>
          <p className="text-mint-tint text-lg mt-5 max-w-xl">{f.lede}</p>
          <button
            onClick={() => navigate(user ? "/app" : "/signup")}
            data-testid="feature-cta-top"
            className="mt-8 inline-flex items-center gap-2 bg-mint text-brand-dark font-semibold rounded-full px-6 py-3 hover:bg-white transition-colors"
          >
            {user ? "Go to your circle" : "Start your care circle"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body: bullets + visual */}
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-10 md:items-start">
          <ul className="space-y-3">
            {f.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-ink">
                <Check className="mt-0.5 w-5 h-5 text-mint-dark flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <div className="bg-white border border-line rounded-2xl p-6 shadow-sm">{f.visual}</div>
        </div>

        {f.callout && (
          <div className="mt-10 rounded-2xl border border-line bg-mint-tint/40 p-5">
            <p className="text-sm font-semibold text-brand-dark">{f.callout}</p>
          </div>
        )}

        {/* Explore other features */}
        <div className="mt-14">
          <p className="text-xs font-semibold uppercase tracking-widest text-subtle">Explore more</p>
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            {others.map(([k, other]) => (
              <Link
                key={k}
                to={`/features/${k}`}
                data-testid={`feature-more-${k}`}
                className="bg-white border border-line rounded-2xl p-5 hover:-translate-y-1 transition-transform duration-200"
              >
                <div className="w-10 h-10 rounded-lg bg-mint-tint flex items-center justify-center mb-3">
                  <other.icon className="w-5 h-5 text-mint-dark" />
                </div>
                <h3 className="font-display text-lg text-ink">{other.eyebrow}</h3>
                <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-brand">Learn more <ArrowRight className="w-3.5 h-3.5" /></span>
              </Link>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 bg-brand-dark rounded-3xl p-10 text-center">
          <ShieldCheck className="w-10 h-10 text-mint mx-auto mb-4" />
          <h2 className="font-display text-3xl text-white">HIPAA-conscious by design</h2>
          <p className="text-mint-tint mt-3 max-w-lg mx-auto">Zero-persistence audio, encrypted document storage, and no PHI stored with payments. Built for working caregivers.</p>
          <button
            onClick={() => navigate(user ? "/app" : "/signup")}
            data-testid="feature-cta-bottom"
            className="mt-7 inline-flex items-center gap-2 bg-mint text-brand-dark font-semibold rounded-full px-7 py-3 hover:bg-white transition-colors"
          >
            {user ? "Go to your circle" : "Create a caregiver account"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <p className="text-center text-xs text-subtle mt-10">
          <Link to="/" data-testid="feature-back-home" className="text-brand font-semibold hover:underline">← Back to home</Link>
          {" · "}
          <Link to="/legal" className="text-brand font-semibold hover:underline">Legal &amp; privacy</Link>
        </p>
      </div>

      <MarketingFooter />
    </div>
  );
}
